import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { saveExchange } from '../controller/exchange-controller';

export const useExchangeForm = (navigate, appSettings = { standardShippingCost: 8 }) => {
  // Main workflow states
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Selected order data
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [exchangeProducts, setExchangeProducts] = useState([]);
  
  // Exchange details
  const [exchangeReason, setExchangeReason] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const [additionalFees, setAdditionalFees] = useState(0);
  
  // Summary calculations
  const [summary, setSummary] = useState({
    originalTotal: 0,
    keepingTotal: 0,
    newProductsTotal: 0,
    shippingCost: 0,
    additionalFees: 0,
    newTotal: 0,
    difference: 0,
    productCount: 0
  });

  // Modals
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Calculate summary whenever products change
  useEffect(() => {
    if (exchangeProducts.length > 0 && selectedOrder) {
      calculateSummary();
    }
  }, [exchangeProducts, selectedOrder, additionalFees, isFreeShipping, shippingMethod, appSettings]);

  // Helper: parse a number that may contain comma as decimal separator
  const parseFlexibleNumber = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    const s = String(v).trim().replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };

  // Helper: determine if a value represents a truthy boolean for promo
  const isTruthyFlag = (val) => {
    if (val === true || val === 1) return true;
    if (typeof val === 'string') {
      const l = val.toLowerCase().trim();
      return l === 'true' || l === '1' || l === 'yes' || l === 'y';
    }
    return false;
  };

  // Helper: get promo-aware unit price from a product/order item
  const getPromoAwarePrice = (obj = {}) => {
    // Candidate locations for promo flag and promo price
    const promoFlagCandidates = [obj.enPromo, obj.EnPromo, obj.isPromo, obj.product?.enPromo, obj.product?.EnPromo];
    const promoPriceCandidates = [
      obj.prixVentePromo,
      obj.PrixVentePromo,
      obj.prixPromo,
      obj.prixVentePromoVal,
      obj.product?.prixVentePromo,
      obj.productVariant?.prixVentePromo,
      obj.variant?.prixVentePromo,
      obj.variant?.price,
    ];

    const promoFlag = promoFlagCandidates.some(v => isTruthyFlag(v));

    // find first valid promo price
    for (const cand of promoPriceCandidates) {
      const n = parseFlexibleNumber(cand);
      if (n > 0) {
        if (promoFlag) return n; // only return promo price if promo flag present
      }
    }

    // Fallbacks for normal unit price
    const normalPriceCandidates = [obj.prixUnitaire, obj.price, obj.prixVenteTTC, obj.prix, obj.product?.prixUnitaire, obj.product?.price, obj.variant?.price, obj.priceTTC];
    for (const cand of normalPriceCandidates) {
      const n = parseFlexibleNumber(cand);
      if (n > 0) return n;
    }

    return 0;
  };

  // Handle order selection
// Updated handleOrderSelect function to ensure at least 1 item is initially set for exchange
const handleOrderSelect = (order) => {
  if (!order) return;
  
  setIsLoading(true);
  setSelectedOrder(order);
  
  // Process order items - initialize with keeping all products (none for exchange)
  const orderProducts = order.items?.map(item => ({
    ...item,
    // Prefer promo-aware price if the order item contains promo info
    price: getPromoAwarePrice(item) || parseFlexibleNumber(item.price) || parseFlexibleNumber(item.prixUnitaire),
    originalQuantity: item.quantity,
    keepQuantity: item.quantity, // Initially keep all items
    exchangeQuantity: 0,
    isKeeping: true,
    isExchanging: false,
    selected: false, // All products start as not selected
    action: 'keep'
  })) || [];
  
  setOriginalProducts(orderProducts);
  setExchangeProducts(orderProducts);
  
  calculateSummary();
  setIsLoading(false);
};

  // Add a product to exchange
// Updated handleAddToExchange function to ensure at least 1 item is marked for exchange

// Now when a user selects a product for exchange, make sure to set at least 1 for exchange
const handleAddToExchange = (itemId) => {
  // Find the item in the original order
  const orderItem = selectedOrder.items.find(item => item.id === itemId);
  
  if (!orderItem) {
    console.error('Item not found in order:', itemId);
    return;
  }

  // Check if product is already in exchange list and selected
  const existingProduct = exchangeProducts.find(p => p.id === itemId);
  
  if (existingProduct && existingProduct.selected) {
    // Product is already selected, toggle selection off
    setExchangeProducts(prev => 
      prev.map(p => 
        p.id === itemId 
          ? { 
              ...p, 
              selected: false,
              keepQuantity: p.originalQuantity, // Reset to keeping all when deselected
              action: 'keep'
            }
          : p
      )
    );
  } else {
    // Product is being selected for exchange
    // Set at least 1 item for exchange by default
    setExchangeProducts(prev => 
      prev.map(p => {
        if (p.id === itemId) {
          // Calculate new keepQuantity to ensure at least 1 for exchange
          const newKeepQuantity = p.originalQuantity > 1 ? p.originalQuantity - 1 : 0;
          const newAction = newKeepQuantity === 0 ? 'exchange' : 
                            newKeepQuantity === p.originalQuantity ? 'keep' : 'partial';
          
          return {
            ...p,
            selected: true,
            keepQuantity: newKeepQuantity,
            action: newAction
          };
        }
        return p;
      })
    );
  }
};

  // Remove a product from exchange
  const handleRemoveFromExchange = (itemId) => {
    const updatedProducts = exchangeProducts.map(product => {
      if (product.id === itemId) {
        return {
          ...product,
          selected: false, // Mark as not selected to hide from the exchange list
          keepQuantity: product.originalQuantity, // Reset to default keepQuantity
          action: 'keep' // Reset action to default
        };
      }
      return product;
    });
    
    setExchangeProducts(updatedProducts);
  };

  // Handle product quantity change for an existing product
  const handleQuantityChange = (product, action, type) => {
    const updatedProducts = exchangeProducts.map(p => {
      if (p.id === product.id) {
        if (type === 'keep') {
          let newKeepQuantity = p.keepQuantity;
          if (action === 'increase' && newKeepQuantity < p.originalQuantity) {
            newKeepQuantity += 1;
          } else if (action === 'decrease' && newKeepQuantity > 0) {
            newKeepQuantity -= 1;
          }
          
          return {
            ...p,
            keepQuantity: newKeepQuantity,
            exchangeQuantity: p.originalQuantity - newKeepQuantity,
            action: newKeepQuantity === 0 ? 'exchange' : newKeepQuantity === p.originalQuantity ? 'keep' : 'partial'
          };
        }
      }
      return p;
    });
    
    setExchangeProducts(updatedProducts);
  };

  // Add a new product to the exchange
  const handleProductSelect = (selectedProducts) => {
    console.log('Product structure:', selectedProducts);
    
    const newProductsToAdd = selectedProducts.map(product => {
      // Determine the image URL based on whether it's a variant or not
      let imageUrl;
      console.log('this is the product', product);
      if (product.isVariant && product.variant && product.variant.imageUrl && product.variant.imageUrl.length > 0) {
        // Use variant image if available
        imageUrl = product.variant.imageUrl[0];
      } else if (product.imageUrl) {
        // Use product image if provided
        imageUrl = Array.isArray(product.imageUrl) ? product.imageUrl[0] : product.imageUrl;
      } else {
        // Default image if none available
        imageUrl = 'https://www.incathlab.com/images/products/default_product.png';
      }
      
      // Create a standardized product object for the exchange
      return {
        id: product.id || product.productId,
        productId: product.productId || product.id,
        variantId: product.variantId || (product.variant ? product.variant.id : null),
        name: product.name,
        sku: product.reference || product.sku || '',
        color: product.isVariant && product.variant ? product.variant.name.split(' - ').pop() : '',
        size: product.isVariant && product.variant ? product.variant.name.split(' - ')[0] : '',
        // Use promo-aware price extraction helper when possible
        price: getPromoAwarePrice(product) || parseFlexibleNumber(product.price),
        quantity: product.quantity || 1,
        stock: product.stock,
        originalQuantity: 0,
        keepQuantity: 0,
        selected: true,
        action: 'add',
        image: imageUrl
      };
    });

    setExchangeProducts(prev => {
      // Filter out any duplicates
      const existingIds = prev.map(p => p.id);
      const filteredNewProducts = newProductsToAdd.filter(p => !existingIds.includes(p.id));
      return [...prev, ...filteredNewProducts];
    });
    
    setShowAddProductModal(false);
  };

  // Remove a product from the exchange
  const handleRemoveProduct = (productId) => {
    const updatedProducts = exchangeProducts.filter(p => 
      !(p.id === productId && p.action === 'add')
    );
    
    setExchangeProducts(updatedProducts);
  };

  // Calculate summary based on products
  const calculateSummary = () => {
    if (!selectedOrder || exchangeProducts.length === 0) return;

    const originalProductsTotal = selectedOrder.total || 0;

    // Calculate products being kept from original order (for display only)
    const keepingProductsTotal = exchangeProducts
      .filter(p => p.originalQuantity > 0)
      .reduce((total, product) => {
        if (product.selected) {
          return total + (product.price * product.keepQuantity);
        } else {
          return total + (product.price * product.originalQuantity);
        }
      }, 0);

    // New products to add
    const newProductsTotal = exchangeProducts
      .filter(p => p.action === 'add')
      .reduce((total, product) => {
        return total + (product.price * product.quantity);
      }, 0);

    // Calculate products being exchanged (returned)
    const exchangedProductsTotal = exchangeProducts
      .filter(p => p.originalQuantity > 0 && p.selected)
      .reduce((total, product) => {
        const exchangeQuantity = product.originalQuantity - product.keepQuantity;
        return total + (product.price * exchangeQuantity);
      }, 0);

    // Use appSettings for shipping cost
    const shippingCost = isFreeShipping ? 0 : appSettings.standardShippingCost;
    
    // SIMPLIFIED CALCULATION: New products - Exchanged products + Shipping
    const difference = newProductsTotal - exchangedProductsTotal + shippingCost;
    
    // For display purposes, calculate the new total (keeping + new products + shipping)
    const newTotal = keepingProductsTotal + newProductsTotal + additionalFees + shippingCost;

    console.log('=== SIMPLIFIED CALCULATION DEBUG ===');
    console.log('New products total:', newProductsTotal);
    console.log('Exchanged products total:', exchangedProductsTotal);
    console.log('Shipping cost:', shippingCost);
    console.log('Simple difference calculation:', `${newProductsTotal} - ${exchangedProductsTotal} + ${shippingCost} = ${difference}`);
    console.log('Display new total:', newTotal);
    
    setSummary({
      originalTotal: originalProductsTotal,
      keepingTotal: keepingProductsTotal,
      newProductsTotal,
      shippingCost,
      additionalFees,
      newTotal,
      difference, // This is now the simplified calculation
      productCount: exchangeProducts.filter(p => p.selected || p.action === 'add').length
    });
  };

  // Prepare exchange data for API
  const prepareExchangeData = () => {
    if (!selectedOrder) return null;

    // Prepare the entet (header) of the exchange with appSettings for shipping cost
    const entet = {
      bonCommandeId: selectedOrder.id,
      dateEchange: new Date().toISOString(),
      reference: `ECH-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
      fraisDeLivraison: isFreeShipping ? 0 : appSettings.standardShippingCost,
      totalTTC: summary.newTotal,
      totalHt: summary.newTotal / 1.19,
      totalTva: summary.newTotal - (summary.newTotal / 1.19),
      totalRemise: 0,
      resteAPayer: Math.max(0, summary.difference),
      etat: 'En attente',
      description: customerNote,
      codeLivraison: shippingMethod
    };

    // Prepare the line items - ONLY EXCHANGED AND NEW PRODUCTS
    const line = [];

    // Add ONLY items being exchanged (returned to you)
    exchangeProducts
      .filter(p => p.originalQuantity > 0 && p.selected)
      .forEach(product => {
        const exchangeQuantity = product.originalQuantity - product.keepQuantity;
        
        // ONLY add if there's actually quantity being exchanged
        if (exchangeQuantity > 0) {
          line.push({
            productId: product.productId || product.id,
            productVariantId: product.variantId,
            quantite: exchangeQuantity,
            prixUnitaire: product.price,
            totalTTC: product.price * exchangeQuantity,
            totalHt: (product.price * exchangeQuantity) / 1.19,
            totalTva: (product.price * exchangeQuantity) - ((product.price * exchangeQuantity) / 1.19),
            totalRemise: 0,
            tauxTva: product.tva || 19,
            tauxRemise: product.remise || 0,
            aEchange: true // Being exchanged (returned)
          });
        }
        
        // DO NOT add products being kept - they stay with the customer
      });

    // Add ONLY new products being given in exchange
    exchangeProducts
      .filter(p => p.action === 'add')
      .forEach(product => {
        line.push({
          productId: product.productId || product.id,
          productVariantId: product.variantId,
          quantite: product.quantity,
          prixUnitaire: product.price,
          totalTTC: product.price * product.quantity,
          totalHt: (product.price * product.quantity) / 1.19,
          totalTva: (product.price * product.quantity) - ((product.price * product.quantity) / 1.19),
          totalRemise: 0,
          tauxTva: product.tva || 19,
          tauxRemise: product.remise || 0,
          aEchange: false // New product (being given in exchange)
        });
      });

    console.log('=== EXCHANGE PAYLOAD DEBUG ===');
    console.log('Products being returned (aEchange: true):', line.filter(p => p.aEchange === true));
    console.log('New products being given (aEchange: false):', line.filter(p => p.aEchange === false));
    console.log('Total line items:', line.length);
    console.log('NO PRODUCTS BEING KEPT - they are not included in exchange');

    return {
      entet,
      line
    };
  };

  // Submit the exchange
  const handleSubmitExchange = async () => {
    if (!selectedOrder) {
      toast.error('Aucune commande sélectionnée');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare exchange data
      const exchangeData = prepareExchangeData();
      
      if (!exchangeData) {
        throw new Error('Erreur lors de la préparation des données d\'échange');
      }
      
      console.log('Submitting exchange data:', exchangeData);
      
      // Save the exchange
      const response = await saveExchange(exchangeData);
      
      toast.success('Échange créé avec succès!');
      navigate('/admin/exchanges');
    } catch (error) {
      console.error('Error creating exchange:', error);
      toast.error('Erreur lors de la création de l\'échange');
    } finally {
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  // Go back to previous step
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Go to next step
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 3) {
      setShowConfirmModal(true);
    }
  };

  return {
    // State
    currentStep,
    setCurrentStep,
    isLoading,
    isSubmitting,
    selectedOrder,
    originalProducts,
    exchangeProducts,
    showAddProductModal,
    setShowAddProductModal,
    showConfirmModal,
    setShowConfirmModal,
    exchangeReason,
    setExchangeReason,
    customerNote,
    setCustomerNote,
    shippingMethod,
    setShippingMethod,
    isFreeShipping,
    setIsFreeShipping,
    additionalFees,
    setAdditionalFees,
    summary,
    
    // Actions
    handleOrderSelect,
    handleAddToExchange,
    handleRemoveFromExchange,
    handleQuantityChange,
    handleProductSelect,
    handleRemoveProduct,
    handleSubmitExchange,
    handleBack,
    handleNext
  };
};